import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  useDroppable,
  useSensor,
  useSensors,
  type Announcements,
  type CollisionDetection,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
  type KeyboardCoordinateGetter,
  type UniqueIdentifier,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Check, ChevronDown, GripVertical, Pencil, Plus, Trash2, X } from 'lucide-react';
import { ModaleConfirmation } from '../contrats/ModaleConfirmation';
import { useToast } from '../ui/useToast';
import { ErreurApi } from '../../lib/api';
import {
  useEnregistrerOrdre,
  useGesteProgramme,
  type Cours,
  type GesteProgramme,
  type LeconCours,
  type OrdreProgramme,
} from '../../lib/useCours';

/**
 * Étape « Programme pédagogique » de « Créer un cours » (TF-442) : les modules
 * du cours et leurs leçons, réordonnés au glisser-déposer — souris, doigt
 * (poignée seule, la page défile ailleurs) ou clavier. Une leçon peut changer
 * de module. L'ordre est enregistré dès que l'élément est déposé ; si l'API le
 * refuse, l'ordre enregistré revient à l'écran.
 */

interface ModuleAffiche {
  id: string;
  titre: string;
  lecons: LeconCours[];
}

type TypeElement = 'module' | 'lecon';

// Identifiants du glisser-déposer : modules, leçons et zone de dépôt d'un
// module vide ne doivent pas se confondre.
const idModule = (id: string) => `m:${id}`;
const idLecon = (id: string) => `l:${id}`;
const idZone = (moduleId: string) => `z:${moduleId}`;

function lire(id: UniqueIdentifier): { type: TypeElement | 'zone'; id: string } {
  const texte = String(id);
  const prefixe = texte.slice(0, 2);
  return { type: prefixe === 'm:' ? 'module' : prefixe === 'l:' ? 'lecon' : 'zone', id: texte.slice(2) };
}

/** Un module se dépose parmi les modules ; une leçon parmi les leçons ou dans un module vide. */
function memeFamille(glisse: UniqueIdentifier, cible: UniqueIdentifier): boolean {
  return (lire(glisse).type === 'module') === (lire(cible).type === 'module');
}

const detection: CollisionDetection = (arguments_) =>
  closestCorners({
    ...arguments_,
    droppableContainers: arguments_.droppableContainers.filter((conteneur) =>
      memeFamille(arguments_.active.id, conteneur.id),
    ),
  });

/**
 * Au clavier, la même règle : sans elle, la flèche vers le bas poserait un
 * module sur sa propre première leçon, et il ne bougerait pas.
 */
const coordonneesClavier: KeyboardCoordinateGetter = (evenement, arguments_) => {
  const conteneurs = arguments_.context.droppableContainers;
  const filtres = Object.assign(Object.create(conteneurs) as typeof conteneurs, {
    get: (id: UniqueIdentifier) => conteneurs.get(id),
    getEnabled: () => conteneurs.getEnabled().filter((conteneur) => memeFamille(arguments_.active, conteneur.id)),
  });
  return sortableKeyboardCoordinates(evenement, {
    ...arguments_,
    context: { ...arguments_.context, droppableContainers: filtres },
  });
};

function copie(modules: Cours['modules']): ModuleAffiche[] {
  return modules.map((module) => ({ id: module.id, titre: module.titre, lecons: [...module.lecons] }));
}

function ordreDe(modules: ModuleAffiche[]): OrdreProgramme {
  return { modules: modules.map((module) => ({ id: module.id, lecons: module.lecons.map((lecon) => lecon.id) })) };
}

function memeOrdre(a: ModuleAffiche[], b: ModuleAffiche[]): boolean {
  return JSON.stringify(ordreDe(a)) === JSON.stringify(ordreDe(b));
}

function moduleDeLaLecon(modules: ModuleAffiche[], leconId: string): number {
  return modules.findIndex((module) => module.lecons.some((lecon) => lecon.id === leconId));
}

function messageErreur(erreur: unknown): string {
  return erreur instanceof ErreurApi ? erreur.message : 'Enregistrement impossible. Réessayez.';
}

const CLASSE_CHAMP =
  'w-full min-w-0 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-codialis-navy focus:border-transparent focus:outline-none focus:ring-2 focus:ring-codialis-mint';

const CLASSE_ICONE =
  'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-codialis-gray transition hover:bg-gray-100 hover:text-codialis-navy disabled:opacity-50 [@media(pointer:coarse)]:h-10 [@media(pointer:coarse)]:w-10';

const CLASSE_POIGNEE =
  'inline-flex h-8 w-8 shrink-0 cursor-grab touch-none items-center justify-center rounded-lg text-codialis-gray transition hover:bg-gray-100 hover:text-codialis-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-codialis-mint active:cursor-grabbing [@media(pointer:coarse)]:h-10 [@media(pointer:coarse)]:w-10';

/** Champ de saisie d'un titre, pour ajouter ou renommer. */
function SaisieTitre({
  libelle,
  initial = '',
  enCours,
  onValider,
  onAnnuler,
}: {
  libelle: string;
  initial?: string;
  enCours: boolean;
  onValider: (titre: string) => void;
  onAnnuler: () => void;
}): JSX.Element {
  const [titre, setTitre] = useState(initial);
  const valider = (evenement: FormEvent) => {
    evenement.preventDefault();
    if (titre.trim()) onValider(titre.trim());
  };
  return (
    <form onSubmit={valider} className="flex min-w-0 flex-1 items-center gap-1.5">
      <input
        autoFocus
        aria-label={libelle}
        value={titre}
        maxLength={150}
        onChange={(evenement) => setTitre(evenement.target.value)}
        onKeyDown={(evenement) => {
          if (evenement.key === 'Escape') onAnnuler();
        }}
        className={CLASSE_CHAMP}
      />
      <button type="submit" disabled={enCours || !titre.trim()} className={CLASSE_ICONE} aria-label="Valider">
        <Check className="h-4 w-4" aria-hidden />
      </button>
      <button type="button" onClick={onAnnuler} className={CLASSE_ICONE} aria-label="Annuler">
        <X className="h-4 w-4" aria-hidden />
      </button>
    </form>
  );
}

function BoutonAjout({ children, onClick, disabled }: { children: ReactNode; onClick: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="inline-flex min-h-[40px] w-full items-center justify-center gap-1.5 rounded-xl border-2 border-dashed border-gray-200 py-2 text-sm text-codialis-gray transition hover:border-codialis-mint hover:text-codialis-teal disabled:opacity-50"
    >
      <Plus className="h-4 w-4" aria-hidden />
      {children}
    </button>
  );
}

type Edition =
  | { cible: 'module'; id: string }
  | { cible: 'lecon'; id: string }
  | { cible: 'nouvelle-lecon'; moduleId: string }
  | { cible: 'nouveau-module' }
  | null;

type Retrait = { cible: 'module'; module: ModuleAffiche } | { cible: 'lecon'; lecon: LeconCours } | null;

function LigneLecon({
  lecon,
  numero,
  edition,
  enCours,
  verrouille,
  onRenommer,
  onEditer,
  onFinEdition,
  onRetirer,
}: {
  lecon: LeconCours;
  numero: string;
  edition: boolean;
  enCours: boolean;
  verrouille: boolean;
  onRenommer: (titre: string) => void;
  onEditer: () => void;
  onFinEdition: () => void;
  onRetirer: () => void;
}): JSX.Element {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: idLecon(lecon.id),
    disabled: verrouille,
  });
  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={`flex items-center gap-2 rounded-lg bg-gray-50 p-2 ${isDragging ? 'opacity-40' : ''}`}
    >
      <button
        type="button"
        ref={setActivatorNodeRef}
        className={CLASSE_POIGNEE}
        aria-label={`Déplacer la leçon « ${lecon.titre} »`}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" aria-hidden />
      </button>
      <span className="w-8 shrink-0 font-mono text-xs text-codialis-gray">{numero}</span>
      {edition ? (
        <SaisieTitre
          libelle="Titre de la leçon"
          initial={lecon.titre}
          enCours={enCours}
          onValider={onRenommer}
          onAnnuler={onFinEdition}
        />
      ) : (
        <>
          <span className="min-w-0 flex-1 truncate text-sm font-medium text-codialis-navy">{lecon.titre}</span>
          <button
            type="button"
            onClick={onEditer}
            disabled={verrouille}
            className={CLASSE_ICONE}
            aria-label={`Renommer la leçon « ${lecon.titre} »`}
          >
            <Pencil className="h-4 w-4" aria-hidden />
          </button>
          <button
            type="button"
            onClick={onRetirer}
            disabled={verrouille}
            className={`${CLASSE_ICONE} hover:text-red-600`}
            aria-label={`Retirer la leçon « ${lecon.titre} »`}
          >
            <Trash2 className="h-4 w-4" aria-hidden />
          </button>
        </>
      )}
    </li>
  );
}

/** Zone de dépôt d'un module vide : sans elle, une leçon ne pourrait pas y entrer. */
function ZoneModuleVide({ moduleId, actif }: { moduleId: string; actif: boolean }): JSX.Element {
  const { setNodeRef, isOver } = useDroppable({ id: idZone(moduleId), disabled: !actif });
  return (
    <div
      ref={setNodeRef}
      className={`rounded-lg border border-dashed px-3 py-3 text-center text-xs text-codialis-gray ${
        isOver ? 'border-codialis-mint bg-codialis-mint/5' : 'border-gray-200'
      }`}
    >
      Aucune leçon. Ajoutez-en une ou déposez-en une ici.
    </div>
  );
}

function CarteModule({
  module,
  numero,
  ouvert,
  onBasculer,
  edition,
  enCours,
  verrouille,
  glisseLecon,
  onEdition,
  onGeste,
  onRetirer,
}: {
  module: ModuleAffiche;
  numero: number;
  ouvert: boolean;
  onBasculer: () => void;
  edition: Edition;
  enCours: boolean;
  verrouille: boolean;
  glisseLecon: boolean;
  onEdition: (edition: Edition) => void;
  onGeste: (geste: GesteProgramme) => void;
  onRetirer: (retrait: Retrait) => void;
}): JSX.Element {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: idModule(module.id),
    disabled: verrouille,
  });
  const renomme = edition?.cible === 'module' && edition.id === module.id;
  const ajoutLecon = edition?.cible === 'nouvelle-lecon' && edition.moduleId === module.id;
  const nombre = module.lecons.length;

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={`rounded-xl border border-gray-100 bg-white ${isDragging ? 'opacity-40' : ''}`}
    >
      <div className="flex items-center gap-2 p-2 sm:gap-3 sm:p-3">
        <button
          type="button"
          ref={setActivatorNodeRef}
          className={CLASSE_POIGNEE}
          aria-label={`Déplacer le module « ${module.titre} »`}
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" aria-hidden />
        </button>
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-codialis-sky text-xs font-bold text-white">
          {numero}
        </div>
        {renomme ? (
          <SaisieTitre
            libelle="Titre du module"
            initial={module.titre}
            enCours={enCours}
            onValider={(titre) => onGeste({ geste: 'renommer-module', moduleId: module.id, titre })}
            onAnnuler={() => onEdition(null)}
          />
        ) : (
          <>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-codialis-navy">{module.titre}</div>
              <div className="text-xs text-codialis-gray">
                {nombre === 0 ? 'Aucune leçon' : nombre === 1 ? '1 leçon' : `${nombre} leçons`}
              </div>
            </div>
            <button
              type="button"
              onClick={() => onEdition({ cible: 'module', id: module.id })}
              disabled={verrouille}
              className={CLASSE_ICONE}
              aria-label={`Renommer le module « ${module.titre} »`}
            >
              <Pencil className="h-4 w-4" aria-hidden />
            </button>
            <button
              type="button"
              onClick={() => onRetirer({ cible: 'module', module })}
              disabled={verrouille}
              className={`${CLASSE_ICONE} hover:text-red-600`}
              aria-label={`Retirer le module « ${module.titre} »`}
            >
              <Trash2 className="h-4 w-4" aria-hidden />
            </button>
            <button
              type="button"
              onClick={onBasculer}
              className={CLASSE_ICONE}
              aria-expanded={ouvert}
              aria-label={ouvert ? `Replier le module « ${module.titre} »` : `Déplier le module « ${module.titre} »`}
            >
              <ChevronDown className={`h-4 w-4 transition ${ouvert ? 'rotate-180' : ''}`} aria-hidden />
            </button>
          </>
        )}
      </div>

      {ouvert && (
        <div className="space-y-2 px-2 pb-2 sm:px-3 sm:pb-3">
          <SortableContext items={module.lecons.map((lecon) => idLecon(lecon.id))} strategy={verticalListSortingStrategy}>
            {nombre === 0 ? (
              <ZoneModuleVide moduleId={module.id} actif={glisseLecon} />
            ) : (
              <ul className="space-y-2">
                {module.lecons.map((lecon, index) => (
                  <LigneLecon
                    key={lecon.id}
                    lecon={lecon}
                    numero={`${numero}.${index + 1}`}
                    edition={edition?.cible === 'lecon' && edition.id === lecon.id}
                    enCours={enCours}
                    verrouille={verrouille}
                    onEditer={() => onEdition({ cible: 'lecon', id: lecon.id })}
                    onFinEdition={() => onEdition(null)}
                    onRenommer={(titre) => onGeste({ geste: 'renommer-lecon', leconId: lecon.id, titre })}
                    onRetirer={() => onRetirer({ cible: 'lecon', lecon })}
                  />
                ))}
              </ul>
            )}
          </SortableContext>
          {ajoutLecon ? (
            <div className="flex items-center gap-2 rounded-lg bg-gray-50 p-2">
              <SaisieTitre
                key={nombre}
                libelle="Titre de la nouvelle leçon"
                enCours={enCours}
                onValider={(titre) => onGeste({ geste: 'ajouter-lecon', moduleId: module.id, titre })}
                onAnnuler={() => onEdition(null)}
              />
            </div>
          ) : (
            <BoutonAjout onClick={() => onEdition({ cible: 'nouvelle-lecon', moduleId: module.id })} disabled={verrouille}>
              Ajouter une leçon
            </BoutonAjout>
          )}
        </div>
      )}
    </li>
  );
}

/** Copie de l'élément saisi, qui suit le pointeur ou le doigt. */
function ApercuGlisse({ libelle, numero, module }: { libelle: string; numero: string; module: boolean }): JSX.Element {
  return (
    <div
      className={`flex items-center gap-2 rounded-xl border border-codialis-mint bg-white p-2 shadow-2xl ${
        module ? 'sm:p-3' : ''
      }`}
    >
      <span className={CLASSE_POIGNEE}>
        <GripVertical className="h-4 w-4" aria-hidden />
      </span>
      {module ? (
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-codialis-sky text-xs font-bold text-white">
          {numero}
        </span>
      ) : (
        <span className="w-8 shrink-0 font-mono text-xs text-codialis-gray">{numero}</span>
      )}
      <span className={`min-w-0 flex-1 truncate text-sm text-codialis-navy ${module ? 'font-semibold' : 'font-medium'}`}>
        {libelle}
      </span>
    </div>
  );
}

export function ProgrammeCours({ cours }: { cours: Cours }): JSX.Element {
  const toast = useToast();
  const enregistrerOrdre = useEnregistrerOrdre(cours.id);
  const geste = useGesteProgramme(cours.id);
  const [modules, setModules] = useState<ModuleAffiche[]>(() => copie(cours.modules));
  const [replies, setReplies] = useState<ReadonlySet<string>>(new Set());
  const [edition, setEdition] = useState<Edition>(null);
  const [retrait, setRetrait] = useState<Retrait>(null);
  const [actif, setActif] = useState<UniqueIdentifier | null>(null);

  // Le programme enregistré, et ce qui empêche de l'afficher : un glisser en
  // cours ou un ordre en route vers l'API. Sinon l'écran reprendrait l'ordre
  // de la base sous le doigt, ou entre deux enregistrements rapprochés.
  const enregistre = useRef(cours.modules);
  enregistre.current = cours.modules;
  const enGlisse = useRef(false);
  const avantGlisse = useRef<ModuleAffiche[] | null>(null);
  const envoi = useRef(false);
  const enAttente = useRef<OrdreProgramme | null>(null);

  useEffect(() => {
    if (!enGlisse.current && !envoi.current) setModules(copie(cours.modules));
  }, [cours.modules]);

  const capteurs = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: coordonneesClavier }),
  );

  /** Envoie l'ordre ; un ordre déposé pendant l'envoi part juste après, seul le dernier compte. */
  async function enregistrer(ordre: OrdreProgramme): Promise<void> {
    if (envoi.current) {
      enAttente.current = ordre;
      return;
    }
    envoi.current = true;
    let prochain: OrdreProgramme | null = ordre;
    let dernier: Cours | null = null;
    try {
      while (prochain) {
        const courant: OrdreProgramme = prochain;
        enAttente.current = null;
        dernier = await enregistrerOrdre.mutateAsync(courant);
        prochain = enAttente.current;
      }
      envoi.current = false;
      if (dernier && !enGlisse.current) setModules(copie(dernier.modules));
    } catch (erreur) {
      envoi.current = false;
      enAttente.current = null;
      if (!enGlisse.current) setModules(copie(enregistre.current));
      toast(messageErreur(erreur));
    }
  }

  function libelleDe(id: UniqueIdentifier): string {
    const element = lire(id);
    if (element.type === 'lecon') {
      const lecon = modules.flatMap((module) => module.lecons).find((candidate) => candidate.id === element.id);
      return `la leçon « ${lecon?.titre ?? ''} »`;
    }
    const module = modules.find((candidat) => candidat.id === element.id);
    return element.type === 'zone' ? `le module vide « ${module?.titre ?? ''} »` : `le module « ${module?.titre ?? ''} »`;
  }

  const annonces: Announcements = {
    onDragStart: ({ active }) => `Vous déplacez ${libelleDe(active.id)}.`,
    onDragOver: ({ active, over }) =>
      over ? `${libelleDe(active.id)} est au niveau de ${libelleDe(over.id)}.` : `${libelleDe(active.id)} est hors de la liste.`,
    onDragEnd: ({ active, over }) =>
      over ? `${libelleDe(active.id)} est déposé à la place de ${libelleDe(over.id)}.` : `${libelleDe(active.id)} est déposé.`,
    onDragCancel: ({ active }) => `Déplacement de ${libelleDe(active.id)} annulé.`,
  };

  function debut({ active }: DragStartEvent): void {
    enGlisse.current = true;
    avantGlisse.current = modules;
    setActif(active.id);
    setEdition(null);
  }

  // Une leçon qui passe au-dessus d'un autre module y entre tout de suite :
  // elle prend sa place parmi ses leçons pendant le glisser.
  function survol({ active, over }: DragOverEvent): void {
    if (!over) return;
    const glisse = lire(active.id);
    const dessous = lire(over.id);
    if (glisse.type !== 'lecon' || dessous.type === 'module') return;
    setModules((courants) => {
      const source = moduleDeLaLecon(courants, glisse.id);
      const cible =
        dessous.type === 'zone'
          ? courants.findIndex((module) => module.id === dessous.id)
          : moduleDeLaLecon(courants, dessous.id);
      if (source < 0 || cible < 0 || source === cible) return courants;
      const lecon = courants[source]!.lecons.find((candidate) => candidate.id === glisse.id)!;
      const position =
        dessous.type === 'zone'
          ? courants[cible]!.lecons.length
          : courants[cible]!.lecons.findIndex((candidate) => candidate.id === dessous.id);
      return courants.map((module, index) => {
        if (index === source) return { ...module, lecons: module.lecons.filter((candidate) => candidate.id !== glisse.id) };
        if (index === cible) {
          const lecons = [...module.lecons];
          lecons.splice(position, 0, lecon);
          return { ...module, lecons };
        }
        return module;
      });
    });
  }

  function fin({ active, over }: DragEndEvent): void {
    enGlisse.current = false;
    setActif(null);
    const avant = avantGlisse.current;
    avantGlisse.current = null;
    let apres = modules;
    if (over && active.id !== over.id) {
      const glisse = lire(active.id);
      const dessous = lire(over.id);
      if (glisse.type === 'module' && dessous.type === 'module') {
        apres = arrayMove(
          modules,
          modules.findIndex((module) => module.id === glisse.id),
          modules.findIndex((module) => module.id === dessous.id),
        );
      } else if (glisse.type === 'lecon' && dessous.type === 'lecon') {
        const index = moduleDeLaLecon(modules, glisse.id);
        if (index >= 0 && index === moduleDeLaLecon(modules, dessous.id)) {
          const lecons = modules[index]!.lecons;
          apres = modules.map((module, rang) =>
            rang === index
              ? {
                  ...module,
                  lecons: arrayMove(
                    lecons,
                    lecons.findIndex((lecon) => lecon.id === glisse.id),
                    lecons.findIndex((lecon) => lecon.id === dessous.id),
                  ),
                }
              : module,
          );
        }
      }
    }
    setModules(apres);
    if (avant && !memeOrdre(avant, apres)) void enregistrer(ordreDe(apres));
  }

  function annulation(): void {
    enGlisse.current = false;
    setActif(null);
    if (avantGlisse.current) setModules(avantGlisse.current);
    avantGlisse.current = null;
  }

  async function executer(demande: GesteProgramme): Promise<void> {
    try {
      await geste.mutateAsync(demande);
      setRetrait(null);
      // Un ajout laisse la saisie ouverte pour enchaîner le suivant.
      if (demande.geste === 'ajouter-module') setEdition({ cible: 'nouveau-module' });
      else if (demande.geste === 'ajouter-lecon') setEdition({ cible: 'nouvelle-lecon', moduleId: demande.moduleId });
      else setEdition(null);
    } catch (erreur) {
      toast(messageErreur(erreur));
    }
  }

  const verrouille = geste.isPending;
  const glisse = actif === null ? null : lire(actif);
  const apercu = (() => {
    if (!glisse) return null;
    if (glisse.type === 'module') {
      const index = modules.findIndex((module) => module.id === glisse.id);
      return index < 0 ? null : { libelle: modules[index]!.titre, numero: String(index + 1), module: true };
    }
    const index = moduleDeLaLecon(modules, glisse.id);
    if (index < 0) return null;
    const rang = modules[index]!.lecons.findIndex((lecon) => lecon.id === glisse.id);
    return { libelle: modules[index]!.lecons[rang]!.titre, numero: `${index + 1}.${rang + 1}`, module: false };
  })();

  return (
    <div className="space-y-3">
      {modules.length === 0 && (
        <p className="rounded-xl bg-gray-50 px-4 py-3 text-sm text-codialis-gray">
          Le programme est vide. Ajoutez un premier module, puis ses leçons.
        </p>
      )}

      <DndContext
        sensors={capteurs}
        collisionDetection={detection}
        accessibility={{
          announcements: annonces,
          screenReaderInstructions: {
            draggable:
              'Pour déplacer un module ou une leçon, appuyez sur Espace ou Entrée, déplacez-le avec les flèches, ' +
              'puis appuyez de nouveau sur Espace ou Entrée pour le déposer, ou sur Échap pour annuler.',
          },
        }}
        onDragStart={debut}
        onDragOver={survol}
        onDragEnd={fin}
        onDragCancel={annulation}
      >
        <SortableContext items={modules.map((module) => idModule(module.id))} strategy={verticalListSortingStrategy}>
          <ul className="space-y-3">
            {modules.map((module, index) => (
              <CarteModule
                key={module.id}
                module={module}
                numero={index + 1}
                ouvert={!replies.has(module.id)}
                onBasculer={() =>
                  setReplies((courants) => {
                    const suivants = new Set(courants);
                    if (suivants.has(module.id)) suivants.delete(module.id);
                    else suivants.add(module.id);
                    return suivants;
                  })
                }
                edition={edition}
                enCours={geste.isPending}
                verrouille={verrouille}
                glisseLecon={glisse?.type === 'lecon'}
                onEdition={setEdition}
                onGeste={(demande) => void executer(demande)}
                onRetirer={setRetrait}
              />
            ))}
          </ul>
        </SortableContext>
        <DragOverlay>{apercu && <ApercuGlisse {...apercu} />}</DragOverlay>
      </DndContext>

      {edition?.cible === 'nouveau-module' ? (
        <div className="flex items-center gap-2 rounded-xl border border-gray-100 p-2 sm:p-3">
          <SaisieTitre
            key={modules.length}
            libelle="Titre du nouveau module"
            enCours={geste.isPending}
            onValider={(titre) => void executer({ geste: 'ajouter-module', titre })}
            onAnnuler={() => setEdition(null)}
          />
        </div>
      ) : (
        <BoutonAjout onClick={() => setEdition({ cible: 'nouveau-module' })} disabled={verrouille}>
          Ajouter un module
        </BoutonAjout>
      )}

      <ModaleConfirmation
        ouverte={retrait !== null}
        titre={retrait?.cible === 'module' ? 'Retirer le module' : 'Retirer la leçon'}
        message={
          retrait?.cible === 'module'
            ? retrait.module.lecons.length > 0
              ? `Le module « ${retrait.module.titre} » et ses ${retrait.module.lecons.length} leçon(s) seront retirés du cours.`
              : `Le module « ${retrait.module.titre} » sera retiré du cours.`
            : retrait?.cible === 'lecon'
              ? `La leçon « ${retrait.lecon.titre} » sera retirée du cours.`
              : ''
        }
        libelleConfirmer="Retirer"
        enCours={geste.isPending}
        onAnnuler={() => setRetrait(null)}
        onConfirmer={() => {
          if (retrait?.cible === 'module') void executer({ geste: 'retirer-module', moduleId: retrait.module.id });
          if (retrait?.cible === 'lecon') void executer({ geste: 'retirer-lecon', leconId: retrait.lecon.id });
        }}
      />
    </div>
  );
}
